import { useState, useEffect, useCallback, useRef } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { profileSharesApi } from '@/shared/api/profileShares';
import { useAuthStore } from '@/store/authStore';
import { useSetupGuideStore } from '@/store/setupGuideStore';
import { useThemeLogo } from '@/styles/themes';
import { CommandPalette } from '@/shared/ui';
import { SetupGuideModal } from '@/features/onboarding/guide/SetupGuideModal';
import { UpgradeDialog } from '@/features/employer/billing/UpgradeDialog';
import { AnnouncementBanner } from '@/features/announcements/AnnouncementBanner';
import { AnnouncementModal } from '@/features/announcements/AnnouncementModal';
import styles from './AppLayout.module.css';

// ─── Nav config ──────────────────────────────────────────────────────────────

const EMPLOYER_NAV = [
  { path: '/dashboard', icon: 'fa-gauge-high', labelKey: 'nav.dashboard' },
  { path: '/employees', icon: 'fa-users', labelKey: 'nav.employees' },
  { path: '/shifts', icon: 'fa-clock', labelKey: 'nav.shifts' },
  { path: '/salary', icon: 'fa-file-invoice-dollar', labelKey: 'nav.salary' },
  { path: '/taxes', icon: 'fa-percent', labelKey: 'nav.taxes' },
  // Last, after the person tabs: Reports is the one tab kept apart from the common shape — a
  // matrix explorer, not an overview with rows per person.
  { path: '/reports', icon: 'fa-chart-bar', labelKey: 'nav.reports' },
] as const;

const EMPLOYEE_NAV = [
  { path: '/my/dashboard', icon: 'fa-gauge-high', labelKey: 'nav.myDashboard' },
  { path: '/my/employers', icon: 'fa-building', labelKey: 'nav.myEmployers' },
  { path: '/my/shifts', icon: 'fa-clock', labelKey: 'nav.myShifts' },
  { path: '/my/pay', icon: 'fa-file-invoice-dollar', labelKey: 'nav.myPay' },
  { path: '/my/taxes', icon: 'fa-percent', labelKey: 'nav.myTaxes' },
] as const;

const ADMIN_NAV = [
  { path: '/admin', icon: 'fa-shield-halved', labelKey: 'nav.admin' },
  { path: '/admin/tax', icon: 'fa-scale-balanced', labelKey: 'nav.taxOps' },
] as const;

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function Sidebar({ onOpenPalette }: { onOpenPalette: () => void }) {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);
  const logout = useAuthStore((s) => s.logout);
  const logoSrc = useThemeLogo();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const openGuide = useSetupGuideStore((s) => s.openGuide);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu on outside click
  useEffect(() => {
    function onMouseDown(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    if (userMenuOpen) document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [userMenuOpen]);

  const navItems =
    role === 'employee' ? EMPLOYEE_NAV : role === 'admin' ? ADMIN_NAV : EMPLOYER_NAV;

  function handleLogout() {
    logout();
    // Wipe cached queries so the next account in this browser starts clean.
    queryClient.clear();
    navigate('/login', { replace: true });
  }

  const initials = user
    ? `${user.firstName?.[0] ?? ''}${user.lastName?.[0] ?? ''}`.toUpperCase() || '?'
    : '?';

  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  return (
    <>
      {/* Mobile top bar */}
      <header className={styles.mobileBar}>
        <button
          className={styles.mobileMenuBtn}
          onClick={() => setMobileOpen(true)}
          aria-label={t('layout.openMenu')}
        >
          <i className="fa-solid fa-bars" />
        </button>
        <span className={styles.mobileBrand}><img src={logoSrc} alt="Casalary" className={styles.mobileLogo} /></span>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <button className={styles.mobileAvatarBtn} onClick={() => setSheetOpen(true)} aria-label={t('menu.profile')}>
            <span className={styles.avatarSm}>{initials}</span>
          </button>
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className={styles.overlay} onClick={() => setMobileOpen(false)} />
      )}

      {/* Mobile user menu bottom sheet */}
      {sheetOpen && (
        <>
          <div className={styles.sheetOverlay} onClick={() => setSheetOpen(false)} />
          <div className={styles.userSheet} role="dialog" aria-label={t('menu.profile')}>
            <div className={styles.userSheetHandle} />
            <div className={styles.userSheetHeader}>
              <span className={styles.userSheetAvatar}>{initials}</span>
              <div>
                <div className={styles.userSheetName}>{fullName}</div>
                <div className={styles.userSheetEmail}>{user?.email}</div>
              </div>
            </div>
            {role === 'admin' && (
              <button className={styles.userSheetItem} onClick={() => { setSheetOpen(false); navigate('/admin'); }}>
                <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                <span>{t('menu.adminDashboard')}</span>
              </button>
            )}
            <button className={styles.userSheetItem} onClick={() => { setSheetOpen(false); navigate('/account'); }}>
              <i className="fa-solid fa-circle-user" aria-hidden="true" />
              <span>{t('menu.account')}</span>
            </button>
            {role === 'employer' && (
              <button className={styles.userSheetItem} onClick={() => { setSheetOpen(false); navigate('/settings'); }}>
                <i className="fa-solid fa-house-chimney" aria-hidden="true" />
                <span>{t('menu.employer')}</span>
              </button>
            )}
            {role !== 'admin' && (
              <button className={styles.userSheetItem} onClick={() => { setSheetOpen(false); openGuide(); }}>
                <i className="fa-solid fa-compass" aria-hidden="true" />
                <span>{t('guide.launch')}</span>
              </button>
            )}
            <div className={styles.userSheetDivider} />
            <button className={`${styles.userSheetItem} ${styles.userSheetItemDanger}`} onClick={() => { setSheetOpen(false); handleLogout(); }}>
              <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
              <span>{t('menu.logout')}</span>
            </button>
          </div>
        </>
      )}

      {/* Sidebar panel */}
      <nav className={`${styles.sidebar} ${mobileOpen ? styles.open : ''}`} aria-label="Main navigation">
        <button
          className={styles.mobileClose}
          onClick={() => setMobileOpen(false)}
          aria-label={t('layout.closeMenu')}
        >
          <i className="fa-solid fa-xmark" />
        </button>

        {/* Brand */}
        <div className={styles.brand}>
          <img src={logoSrc} alt="Casalary" className={styles.brandLogo} />
        </div>

        {/* Command palette trigger */}
        <button className={styles.searchBtn} onClick={onOpenPalette} aria-label={t('layout.search')}>
          <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
          <span>{t('layout.search')}</span>
          <kbd className={styles.kbdHint}>⌘K</kbd>
        </button>

        {/* Nav links */}
        <ul className={styles.navList} role="list">
          {navItems.map((item) => (
            <li key={item.path}>
              <NavLink
                to={item.path}
                data-tour={`nav${item.path.replace(/\//g, '-')}`}
                className={({ isActive }) =>
                  `${styles.navLink} ${isActive ? styles.active : ''}`
                }
                onClick={() => setMobileOpen(false)}
              >
                <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
                <span>{t(item.labelKey, item.labelKey.split('.')[1])}</span>
              </NavLink>
            </li>
          ))}
        </ul>

        {/* Bottom section */}
        <div className={styles.sidebarBottom}>
          {/* User card + popover menu */}
          <div className={styles.userMenuWrap} ref={userMenuRef}>
            {userMenuOpen && (
              <div className={styles.userMenu} role="menu">
                {role === 'admin' && (
                  <button
                    className={styles.userMenuItem}
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); navigate('/admin'); }}
                  >
                    <i className="fa-solid fa-shield-halved" aria-hidden="true" />
                    <span>{t('menu.adminDashboard')}</span>
                  </button>
                )}
                <button
                  className={styles.userMenuItem}
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); navigate('/account'); }}
                >
                  <i className="fa-solid fa-circle-user" aria-hidden="true" />
                  <span>{t('menu.account')}</span>
                </button>
                {role === 'employer' && (
                  <button
                    className={styles.userMenuItem}
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                  >
                    <i className="fa-solid fa-house-chimney" aria-hidden="true" />
                    <span>{t('menu.employer')}</span>
                  </button>
                )}
                {role !== 'admin' && (
                  <button
                    className={styles.userMenuItem}
                    role="menuitem"
                    onClick={() => { setUserMenuOpen(false); openGuide(); }}
                  >
                    <i className="fa-solid fa-compass" aria-hidden="true" />
                    <span>{t('guide.launch')}</span>
                  </button>
                )}
                <div className={styles.userMenuDivider} />
                <button
                  className={`${styles.userMenuItem} ${styles.userMenuItemDanger}`}
                  role="menuitem"
                  onClick={() => { setUserMenuOpen(false); handleLogout(); }}
                >
                  <i className="fa-solid fa-right-from-bracket" aria-hidden="true" />
                  <span>{t('menu.logout')}</span>
                </button>
              </div>
            )}
            <button
              className={styles.userBtn}
              onClick={() => setUserMenuOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={userMenuOpen}
            >
              <span className={styles.avatar}>{initials}</span>
              <span className={styles.userInfo}>
                <span className={styles.userName}>{fullName}</span>
                <span className={styles.userEmail}>{user?.email}</span>
              </span>
              {role && (
                <span
                  className={styles.roleIcon}
                  title={role === 'employee' ? t('roleBadge.employee') : role === 'admin' ? t('roleBadge.admin') : t('roleBadge.employer')}
                  aria-label={role === 'employee' ? t('roleBadge.employee') : role === 'admin' ? t('roleBadge.admin') : t('roleBadge.employer')}
                >
                  <i className={`fa-solid ${role === 'employee' ? 'fa-id-badge' : role === 'admin' ? 'fa-shield-halved' : 'fa-briefcase'}`} aria-hidden="true" />
                </span>
              )}
              <i className="fa-solid fa-ellipsis-vertical" aria-hidden="true" />
            </button>
          </div>
        </div>
      </nav>
    </>
  );
}

// ─── Bottom nav (mobile) ──────────────────────────────────────────────────────

function BottomNav({ navItems }: { navItems: readonly { path: string; icon: string; labelKey: string }[] }) {
  const { t } = useTranslation();
  return (
    <nav className={styles.bottomNav} aria-label="Main navigation">
      {navItems.map((item) => (
        <NavLink
          key={item.path}
          to={item.path}
          data-tour={`nav${item.path.replace(/\//g, '-')}`}
          className={({ isActive }) =>
            `${styles.bottomNavItem} ${isActive ? styles.active : ''}`
          }
        >
          <i className={`fa-solid ${item.icon}`} aria-hidden="true" />
          <span>{t(item.labelKey, item.labelKey.split('.')[1])}</span>
        </NavLink>
      ))}
    </nav>
  );
}

// ─── Pending share code ───────────────────────────────────────────────────────

/**
 * Someone who signed up through a Casalary card link (register?code=) carries that code on
 * their account until they redeem it. Once, after login, send them to the review step.
 */
function PendingShareCodeRedirect() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useAuthStore((s) => s.token);
  const { data } = useQuery({
    queryKey: ['profile-share', 'pending', 'probe'],
    queryFn: profileSharesApi.pending,
    enabled: Boolean(token),
    staleTime: Infinity,
    retry: false,
  });
  useEffect(() => {
    if (data && !data.expired && !location.pathname.startsWith('/c/')) navigate('/c/pending', { replace: true });
  }, [data, location.pathname, navigate]);
  return null;
}

// ─── AppLayout ────────────────────────────────────────────────────────────────

export function AppLayout() {
  const [paletteOpen, setPaletteOpen] = useState(false);
  const role = useAuthStore((s) => s.role);

  const openPalette = useCallback(() => setPaletteOpen(true), []);

  const navItems =
    role === 'employee' ? EMPLOYEE_NAV : role === 'admin' ? ADMIN_NAV : EMPLOYER_NAV;

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return (
    <div className={styles.root}>
      <Sidebar onOpenPalette={openPalette} />
      <main className={styles.main}>
        <AnnouncementBanner />
        <PendingShareCodeRedirect />
        <Outlet />
      </main>
      <BottomNav navItems={navItems} />
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
      <SetupGuideModal />
      <UpgradeDialog />
      <AnnouncementModal />
    </div>
  );
}
